import { Modal, Select, Text, LegacyStack, Button, IndexTable, Banner, Divider } from '@shopify/polaris';
import { useContext, useMemo, useState } from 'react';
import { postReconcileLosses } from '../../../services/productService';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';

const LOST_DEPOSIT_ID = -390;

// Reconcilia extraviados entre tallas de la MISMA familia: cancela el faltante de una talla
// (saldo 390 positivo) contra el sobrante de otra (saldo 390 negativo).
const ReconcileLossesModal = ({ open, onClose, product, onDone }) => {
  const [, dispatchToast] = useContext(ToastContext);
  const [lines, setLines] = useState([]); // [{ deficitBarcode, surplusBarcode, qty }]
  const [deficit, setDeficit] = useState('');
  const [surplus, setSurplus] = useState('');
  const [qty, setQty] = useState('1');
  const [loading, setLoading] = useState(false);

  const lossByBarcode = useMemo(() => {
    const map = {};
    (product?.stock || [])
      .filter((s) => s.deposit.id === LOST_DEPOSIT_ID)
      .forEach((s) => {
        map[s.barcode] = s.qty;
      });
    return map;
  }, [product]);

  const titleOf = (barcode) => product?.variants.find((v) => v.barcode === barcode)?.title ?? barcode;

  // Saldo restante teniendo en cuenta las líneas ya añadidas.
  const remaining = useMemo(() => {
    const r = { ...lossByBarcode };
    lines.forEach((l) => {
      r[l.deficitBarcode] = (r[l.deficitBarcode] ?? 0) - l.qty;
      r[l.surplusBarcode] = (r[l.surplusBarcode] ?? 0) + l.qty;
    });
    return r;
  }, [lossByBarcode, lines]);

  const deficitOptions = product
    ? product.variants
        .filter((v) => (remaining[v.barcode] ?? 0) > 0)
        .map((v) => ({ label: `${v.title} (faltan ${remaining[v.barcode]})`, value: v.barcode }))
    : [];
  const surplusOptions = product
    ? product.variants
        .filter((v) => (remaining[v.barcode] ?? 0) < 0)
        .map((v) => ({ label: `${v.title} (sobran ${-remaining[v.barcode]})`, value: v.barcode }))
    : [];

  const maxQty = deficit && surplus ? Math.min(remaining[deficit] ?? 0, -(remaining[surplus] ?? 0)) : 0;
  const qtyNum = parseInt(qty) || 0;

  // Neto de la familia con las líneas ya añadidas. Si es 0, se cuadra entera.
  const netRemaining = Object.values(remaining).reduce((acc, v) => acc + v, 0);
  const totalDeficit = Object.values(remaining).reduce((acc, v) => acc + (v > 0 ? v : 0), 0);
  const totalSurplus = Object.values(remaining).reduce((acc, v) => acc + (v < 0 ? -v : 0), 0);
  const cuadrable = Math.min(totalDeficit, totalSurplus);

  /**
   * Empareja faltantes con sobrantes de mayor a menor hasta agotar el lado más corto.
   * Greedy a propósito: da igual qué talla se cruce con cuál, porque el asiento resultante
   * es el mismo montante contra la misma cuenta. Lo único que importa es no pasarse de lo
   * que cada talla tiene, y por eso se descuenta sobre `remaining`, que ya incluye las
   * líneas añadidas a mano antes de pulsar el botón.
   */
  const autoBalance = () => {
    const deficits = Object.entries(remaining)
      .filter(([, qty]) => qty > 0)
      .map(([barcode, qty]) => ({ barcode, qty }))
      .sort((a, b) => b.qty - a.qty);
    const surpluses = Object.entries(remaining)
      .filter(([, qty]) => qty < 0)
      .map(([barcode, qty]) => ({ barcode, qty: -qty }))
      .sort((a, b) => b.qty - a.qty);

    const nuevas = [];
    let i = 0;
    let j = 0;
    while (i < deficits.length && j < surpluses.length) {
      const qty = Math.min(deficits[i].qty, surpluses[j].qty);
      if (qty > 0) {
        nuevas.push({ deficitBarcode: deficits[i].barcode, surplusBarcode: surpluses[j].barcode, qty });
        deficits[i].qty -= qty;
        surpluses[j].qty -= qty;
      }
      if (deficits[i].qty === 0) i += 1;
      if (surpluses[j] && surpluses[j].qty === 0) j += 1;
    }
    if (nuevas.length) setLines([...lines, ...nuevas]);
  };

  const addLine = () => {
    if (!deficit || !surplus || qtyNum <= 0 || qtyNum > maxQty) return;
    setLines([...lines, { deficitBarcode: deficit, surplusBarcode: surplus, qty: qtyNum }]);
    setDeficit('');
    setSurplus('');
    setQty('1');
  };

  const handleClose = () => {
    setLines([]);
    setDeficit('');
    setSurplus('');
    setQty('1');
    onClose();
  };

  const handleSubmit = () => {
    if (!lines.length) return;
    setLoading(true);
    postReconcileLosses(product.id, { lines })
      .then(() => {
        dispatchToast({ type: SET_OK, payload: { message: 'Extraviados reconciliados' } });
        onDone && onDone();
        handleClose();
      })
      .catch((e) =>
        dispatchToast({ type: SET_ERROR, payload: { message: e?.response?.data?.message || 'Error al reconciliar' } }),
      )
      .finally(() => setLoading(false));
  };

  if (!product) return null;

  const hasAny = Object.values(lossByBarcode).some((v) => v !== 0);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <>
          <Text variant="headingLg" fontWeight="bold">
            Reconciliar extraviados entre tallas
          </Text>
          <Text variant="bodySm" tone="subdued">
            Cancela el faltante de una talla contra el sobrante de otra (cambio de talla no reportado)
          </Text>
        </>
      }
      primaryAction={{ content: 'Reconciliar', onAction: handleSubmit, disabled: !lines.length, loading }}
      secondaryActions={[{ content: 'Cancelar', onAction: handleClose }]}
    >
      <Modal.Section>
        {!hasAny ? (
          <Banner status="info">Esta familia no tiene extraviados por reconciliar.</Banner>
        ) : (
          <LegacyStack vertical spacing="tight">
            {cuadrable > 0 && (
              <Banner status={netRemaining === 0 ? 'success' : 'warning'}>
                <LegacyStack alignment="center" distribution="equalSpacing">
                  <Text variant="bodyMd">
                    {netRemaining === 0
                      ? `Se cuadra entera: ${cuadrable} uds faltan y las mismas ${cuadrable} sobran.`
                      : `Se pueden cuadrar ${cuadrable} uds. ${
                          netRemaining > 0 ? `Quedarían ${netRemaining} sin cubrir.` : `Sobrarían ${-netRemaining}.`
                        }`}
                  </Text>
                  <Button onClick={autoBalance}>Cuadrar automáticamente</Button>
                </LegacyStack>
              </Banner>
            )}
            <Text variant="headingSm" fontWeight="bold">
              Añadir reconciliación:
            </Text>
            <div className="grid md:grid-cols-[3fr_3fr_2fr_2fr] gap-4 justify-around items-end">
              <div>
                <Text variant="bodySm" tone="subdued">
                  Talla que falta
                </Text>
                <Select
                  options={[{ label: '—', value: '' }, ...deficitOptions]}
                  value={deficit}
                  onChange={setDeficit}
                />
              </div>
              <div>
                <Text variant="bodySm" tone="subdued">
                  Talla que sobra
                </Text>
                <Select
                  options={[{ label: '—', value: '' }, ...surplusOptions]}
                  value={surplus}
                  onChange={setSurplus}
                />
              </div>
              <div>
                <Text variant="bodySm" tone="subdued">
                  Uds (máx {maxQty})
                </Text>
                <input
                  type="number"
                  min="1"
                  max={maxQty}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="w-full border border-gray-400 rounded p-2 text-right"
                />
              </div>
              <Button onClick={addLine} disabled={!deficit || !surplus || qtyNum <= 0 || qtyNum > maxQty}>
                Añadir
              </Button>
            </div>
            <div className="py-4">
              <Divider />
            </div>
            {lines.length > 0 && (
              <IndexTable
                headings={[
                  { title: 'Falta' },
                  { title: 'Sobra' },
                  { title: 'Uds', alignment: 'center' },
                  { title: '', alignment: 'end' },
                ]}
                itemCount={lines.length}
                selectable={false}
              >
                {lines.map((l, i) => (
                  <IndexTable.Row id={String(i)} key={i} position={i} selected={false}>
                    <IndexTable.Cell>{titleOf(l.deficitBarcode)}</IndexTable.Cell>
                    <IndexTable.Cell>{titleOf(l.surplusBarcode)}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <div className="flex justify-center">{l.qty}</div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div className="flex justify-end">
                        <Button variant="plain" onClick={() => setLines(lines.filter((_, k) => k !== i))}>
                          Quitar
                        </Button>
                      </div>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            )}
          </LegacyStack>
        )}
      </Modal.Section>
    </Modal>
  );
};

export default ReconcileLossesModal;
